import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, Checkbox, ListItemText, MenuItem, Stack, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useState } from 'react';

import { useI18n } from '../../../../../../application/language/useI18n';
import { ProjectInfoDomain } from '../store/ProjectInfoDomain';
import { areArraysEqual, getInvitationLink, lowercaseRoleFirst } from './utils';
import { Colors } from '../../../../../design/color/Colors';

export interface IInvitationRolesListProperties {
  domain: ProjectInfoDomain;
  projectId: string;
  handleClose: () => void;
}

export const InvitationRolesList = observer(({ domain, projectId, handleClose }: IInvitationRolesListProperties) => {
  const { translate } = useI18n();
  const [selectedItems, setSelectedItems] = useState<{ id: string; name: string }[]>([]);
  const options = domain.ui.projectRoles.list.map((item) => ({ name: item.name, id: item.id! })) || [];
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleToggle = (option: { id: string; name: string }) => {
    setSelectedItems((prev) =>
      prev.some((item) => item.id === option.id) ? prev.filter((item) => item.id !== option.id) : [...prev, option],
    );
  };

  const onCreateInvitationLink = () => {
    const selectedRolesIds = selectedItems.map((item) => item.id);

    const existingLink = domain.ui.invitationLinks.list.find((item) => {
      const existingLinkRolesIds = item.abledRoles.map((role) => role.id);
      return areArraysEqual(existingLinkRolesIds, selectedRolesIds);
    });

    if (!existingLink) {
      setLoading(true);
      domain
        .createInvitationLink({ project: projectId, abledRoles: selectedItems.map((item) => ({ id: item.id })) })
        .then((res) => domain.getInvitationLinkById(res))
        .then((link) => {
          handleCopy(link);
        });
    } else {
      handleCopy(existingLink);
    }
  };

  const handleCopy = (link) => {
    setLinkCopied(true);
    const invitationLink = getInvitationLink(link);
    navigator.clipboard.writeText(invitationLink);
    setTimeout(handleClose,400)
    domain.fetchInvitationLinks(projectId);
  };

  return (
    <Box sx={{ height: '450px' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: Colors.bgElevated,
          zIndex: 1,
          pl: 3,
        }}
      >
        <Typography>{translate('projectPage.invitationLinkHelperText')}</Typography>
      </Box>

      <Box sx={{ maxHeight: '450px', width: '420px', overflowY: 'auto' }}>
        {options.map((item) => (
          <MenuItem key={item.id} onClick={() => handleToggle(item)}>
            <Checkbox checked={selectedItems.some((selected) => selected.id === item.id)} />
            <ListItemText primary={item.name} />
          </MenuItem>
        ))}
      </Box>

      {!!(selectedItems.length > 0) && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: Colors.bgElevated,
            zIndex: 1,
            p: 3,
          }}
        >
            <Button
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word', color: Colors.primaryLight, width: '100%' }}
              disabled={loading || !selectedItems.length}
              onClick={onCreateInvitationLink}
            >
              <Stack direction="row" gap={2} width='100%'>
                {linkCopied ? (
                  <CheckIcon sx={{ fontSize: 20, color: Colors.primaryLight }} />
                ) : (
                  <ContentCopyIcon sx={{  fontSize: 20, color: Colors.primaryLight }} />
                )}
                <Typography sx={{textAlign: 'start'}}>
                  {translate('phrases.linkFor')} {selectedItems.map((item) => lowercaseRoleFirst(item.name)).join(', ')}
                </Typography>
              </Stack>
            </Button>
        </Box>
      )}
    </Box>
  );
});
